import { useState } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { submitFrontLoad, track } from "../../lib/api";
import { BUSINESS } from "../../data/site";

const SIZES = ["3 Yard", "4 Yard", "6 Yard", "8 Yard", "Not Sure"];
const FREQUENCIES = ["Weekly", "Multiple Times Per Week", "Custom Schedule", "Not Sure Yet"];

function OptionCard({ selected, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      data-testid={testid}
      className={`px-4 py-3 rounded-lg border-2 text-left font-heading font-semibold text-sm transition-all active:scale-[0.98] ${
        selected ? "border-sky-600 bg-sky-50 text-sky-800" : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"
      }`}
    >
      {children}
    </button>
  );
}

const inputCls =
  "w-full px-4 py-3 rounded-lg border-2 border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:border-sky-600 focus:outline-none transition-colors";

export default function FrontLoadForm({ defaultType = "Residential" }) {
  const [form, setForm] = useState({
    serviceType: defaultType,
    size: "",
    frequency: "",
    zip: "",
    name: "",
    phone: "",
    email: "",
    notes: "",
    website: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.size) return setError("Please choose a dumpster size (or 'Not Sure').");
    if (!form.frequency) return setError("Please choose a pickup frequency.");
    if (!form.zip.trim()) return setError("Please enter your ZIP code.");
    if (!form.name.trim()) return setError("Please enter your name.");
    if (!form.phone.trim()) return setError("Please enter your phone number.");
    if (!/.+@.+\..+/.test(form.email)) return setError("Please enter a valid email address.");
    setError("");
    setSubmitting(true);
    try {
      await submitFrontLoad({
        service_type: form.serviceType,
        size: form.size,
        frequency: form.frequency,
        zip_code: form.zip,
        name: form.name,
        phone: form.phone,
        email: form.email,
        notes: form.notes,
        website: form.website,
      });
      track("frontload_request_submit", { service_type: form.serviceType, size: form.size });
      setDone(true);
    } catch {
      setError("Something went wrong. Please call us at (928) 713-2710.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-8 sm:p-10 text-center" data-testid="frontload-success">
        <CheckCircle2 className="h-14 w-14 text-sky-600 mx-auto mb-4" />
        <h3 className="font-heading font-bold text-2xl text-slate-900 mb-2">Request Received</h3>
        <p className="text-slate-600 max-w-md mx-auto">
          Thanks, {form.name.split(" ")[0]}. We'll follow up during business hours (Mon–Fri, 8 AM–5 PM) to confirm your {form.serviceType.toLowerCase()} front-load service and schedule.
        </p>
        <p className="mt-5 text-sm text-slate-500">
          Questions? <a href={BUSINESS.phoneHref} className="font-semibold text-sky-700" data-testid="frontload-success-call">Call {BUSINESS.phone}</a>
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8" data-testid="frontload-form">
      <h3 className="font-heading font-bold text-xl text-slate-900 mb-6">Request Front-Load Service</h3>
      <div className="space-y-6">
        <div>
          <span className="block font-heading font-semibold text-slate-900 mb-2">1. Service Type</span>
          <div className="grid grid-cols-2 gap-2.5">
            {["Residential", "Commercial"].map((t) => (
              <OptionCard key={t} selected={form.serviceType === t} onClick={() => set("serviceType", t)} testid={`fl-type-${t.toLowerCase()}`}>
                {t}
              </OptionCard>
            ))}
          </div>
        </div>
        <div>
          <span className="block font-heading font-semibold text-slate-900 mb-2">2. Dumpster Size</span>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2.5">
            {SIZES.map((s) => (
              <OptionCard key={s} selected={form.size === s} onClick={() => set("size", s)} testid={`fl-size-${s.split(" ")[0].toLowerCase()}`}>
                {s}
              </OptionCard>
            ))}
          </div>
        </div>
        <div>
          <span className="block font-heading font-semibold text-slate-900 mb-2">3. Desired Pickup Frequency</span>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
            {FREQUENCIES.map((f) => (
              <OptionCard key={f} selected={form.frequency === f} onClick={() => set("frequency", f)} testid={`fl-freq-${f.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                {f}
              </OptionCard>
            ))}
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="fl-zip" className="block font-heading font-semibold text-slate-900 mb-2">4. ZIP Code</label>
            <input id="fl-zip" inputMode="numeric" autoComplete="postal-code" value={form.zip} onChange={(e) => set("zip", e.target.value)} className={inputCls} data-testid="fl-zip" placeholder="86301" />
          </div>
          <div>
            <label htmlFor="fl-name" className="block font-heading font-semibold text-slate-900 mb-2">5. Name</label>
            <input id="fl-name" autoComplete="name" value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} data-testid="fl-name" />
          </div>
          <div>
            <label htmlFor="fl-phone" className="block font-heading font-semibold text-slate-900 mb-2">6. Phone</label>
            <input id="fl-phone" type="tel" autoComplete="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} data-testid="fl-phone" />
          </div>
          <div>
            <label htmlFor="fl-email" className="block font-heading font-semibold text-slate-900 mb-2">7. Email</label>
            <input id="fl-email" type="email" autoComplete="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} data-testid="fl-email" />
          </div>
        </div>
        <div>
          <label htmlFor="fl-notes" className="block font-heading font-semibold text-slate-900 mb-2">
            Notes <span className="text-slate-400 font-normal">(optional)</span>
          </label>
          <textarea id="fl-notes" rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} className={inputCls} data-testid="fl-notes" />
        </div>
        <input type="text" name="website" value={form.website} onChange={(e) => set("website", e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" className="absolute opacity-0 h-0 w-0 pointer-events-none" />
        {error && (
          <p role="alert" className="text-sm font-medium text-slate-800 bg-slate-100 border border-slate-300 rounded-lg px-4 py-2.5" data-testid="fl-error">
            {error}
          </p>
        )}
        <button onClick={submit} disabled={submitting} className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-lg bg-sky-600 hover:bg-sky-700 disabled:opacity-60 text-white font-heading font-semibold transition-all active:scale-[0.99]" data-testid="fl-submit">
          {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
          Request Front-Load Service
        </button>
      </div>
    </div>
  );
}
